// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
import { Switch, TextField } from "@mui/material";
// --- Our Modules --------------------------------------------------------------------------------
import type { Lang } from "@/hooks/useLang/types";
import useLang from "@/hooks/useLang";
import useStoreLanguages from "@/hooks/useStoreLanguages";
import useTranslation from "@/hooks/useTranslation";
import type { TyEditorPrice, TyLang } from "@/types";
import { getTextFieldSx, isRtlLang } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type PricesPopupDetails = {
    productName: string;
    prices: TyEditorPrice[];
    onConfirm: (prices: TyEditorPrice[]) => void;
};

type Props = {
    onClose: () => void;
    details?: PricesPopupDetails;
};

// --- Constants ----------------------------------------------------------------------------------

const switchSx = {
    width: 34,
    height: 21,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: "2.5px",
        transitionDuration: "200ms",
        "&.Mui-checked": {
            transform: "translateX(13px)",
            color: "var(--color-white)",
            "& + .MuiSwitch-track": { backgroundColor: "var(--color-brand)", opacity: 1, border: 0 },
        },
    },
    "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.2)" },
    "& .MuiSwitch-track": { borderRadius: "var(--radius-full)", backgroundColor: "var(--color-gray-01)", opacity: 1 },
};

// --- Helpers ------------------------------------------------------------------------------------

/**
 * The editor stacks several tiers in one popup, so its fields run 40px instead of the dashboard's
 * default. Built on top of the shared `getTextFieldSx` rather than replacing it, and kept local so no
 * other form in the app shrinks with it — the label offsets have to move with the height, and they
 * differ per direction, which a stylesheet override cannot express cleanly.
 */
const compactTextFieldSx = (dir: "rtl" | "ltr") => {
    const base = getTextFieldSx(dir);
    return {
        ...base,
        "& .MuiInputLabel-root": {
            ...base["& .MuiInputLabel-root"],
            "&:not(.MuiInputLabel-shrink)": {
                transform: dir === "rtl" ? "translate(-14px, 8px) scale(1)" : "translate(14px, 8px) scale(1)",
            },
        },
        "& .MuiOutlinedInput-root": { ...base["& .MuiOutlinedInput-root"], height: "40px" },
        "& .MuiOutlinedInput-input": { ...base["& .MuiOutlinedInput-input"], padding: "0 14px" },
    };
};

const labelOf = (label: TyLang[] | undefined, lang: Lang) => label?.find((entry) => entry.lang === lang)?.value ?? "";

/**
 * Keeps the value exactly as typed. Trimming here is what used to swallow spaces — " " at the end of
 * "Fruit " collapsed on every keystroke, so a two-word label could not be typed left to right.
 * Trimming happens once, on confirm.
 */
const withLabel = (label: TyLang[] | undefined, lang: Lang, value: string): TyLang[] => {
    const others = (label ?? []).filter((entry) => entry.lang !== lang);
    return value ? [...others, { lang, value }] : others;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * A product's price is a list — "small / medium / large" is one product, not three. A grid cell cannot
 * hold a growing list, so the whole tier list is edited here and written back in one go.
 */
export default function PricesPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { languages } = useStoreLanguages();
    const [prices, setPrices] = useState<TyEditorPrice[]>(() =>
        (details?.prices ?? []).map((price) => ({ ...price, label: [...(price.label ?? [])] }))
    );

    const dir = lang === "ar" ? "rtl" : "ltr";
    const textFieldSx = compactTextFieldSx(dir as "rtl" | "ltr");
    const textFieldSxRtl = compactTextFieldSx("rtl");
    const textFieldSxLtr = compactTextFieldSx("ltr");

    if (!details) return null;

    const patch = (index: number, next: Partial<TyEditorPrice>) =>
        setPrices((rows) => rows.map((row, i) => (i === index ? { ...row, ...next } : row)));

    /** Labels start on — a tier is usually a size, and typing the size is the point of adding one. */
    const addPrice = () => setPrices((rows) => [...rows, { amount: 0, label: [], showLabel: true, discount: null }]);

    const removePrice = (index: number) => setPrices((rows) => rows.filter((_, i) => i !== index));

    const handleConfirm = () => {
        const cleaned = prices
            .filter((price) => Number.isFinite(price.amount))
            .map((price) => {
                const label = (price.label ?? []).map((entry) => ({ ...entry, value: entry.value.trim() })).filter((entry) => entry.value !== "");
                // A tier switched on but left blank would print an empty label on the live menu.
                return { ...price, label, showLabel: label.length > 0 ? price.showLabel : false };
            });

        details.onConfirm(cleaned);
        onClose();
    };

    return (
        <div className="prices-popup">
            <div className="prices-popup-head">
                <p className="prices-popup-title">{t.menuEditor.pricesOf}</p>
                <p className="prices-popup-subtitle">{details.productName || "—"}</p>
            </div>

            <div className="prices-popup-list">
                {prices.length === 0 && <p className="prices-popup-empty">{t.menuEditor.noPricesYet}</p>}

                {prices.map((price, index) => (
                    <div className="prices-popup-row" key={price._id ?? `new-${index}`}>
                        <div className="prices-popup-amount">
                            <TextField
                                fullWidth
                                type="number"
                                label={t.menuEditor.price}
                                value={String(price.amount)}
                                onChange={(event) => patch(index, { amount: Number(event.target.value) || 0 })}
                                slotProps={{ htmlInput: { min: 0, step: 0.01, inputMode: "decimal" } }}
                                sx={textFieldSx}
                            />
                            <button
                                type="button"
                                className="prices-popup-remove"
                                onClick={() => removePrice(index)}
                                aria-label={t.menuEditor.removeRow}
                                title={t.menuEditor.removeRow}
                            >
                                <img src="/icons/close-icon.svg" alt="" width={12} height={12} />
                            </button>
                        </div>

                        <div className="prices-popup-section">
                            <div className="prices-popup-section-head">
                                <span className="prices-popup-section-title">{t.menuEditor.showLabel}</span>
                                <Switch
                                    checked={Boolean(price.showLabel)}
                                    onChange={(event) => patch(index, { showLabel: event.target.checked })}
                                    sx={switchSx}
                                />
                            </div>

                            {price.showLabel && (
                                <div className="prices-popup-labels" dir="ltr">
                                    {languages.map((entry) => (
                                        <TextField
                                            key={entry}
                                            fullWidth
                                            label={`${t.menuEditor.priceLabel} · ${t.languages[entry]}`}
                                            value={labelOf(price.label, entry)}
                                            onChange={(event) => patch(index, { label: withLabel(price.label, entry, event.target.value) })}
                                            sx={isRtlLang(entry) ? textFieldSxRtl : textFieldSxLtr}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <button type="button" className="prices-popup-add" onClick={addPrice}>
                + {t.menuEditor.addPrice}
            </button>

            <div className="prices-popup-actions">
                <button type="button" className="prices-popup-cancel" onClick={onClose}>
                    {t.menuEditor.cancel}
                </button>
                <button type="button" className="prices-popup-confirm" onClick={handleConfirm}>
                    {t.menuEditor.done}
                </button>
            </div>
        </div>
    );
}
